import { useAppDispatch } from '@/hooks';
import classNames from 'classnames';
import Quill, { QuillOptions } from 'quill';
import { Delta, Op, Range } from 'quill/core';
import Keyboard from 'quill/modules/keyboard';
import { forwardRef, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { setAlertNotification } from '@/redux/globalReducer';

import {
  ALLOW_ALL_FILE,
  API_URL,
  MAPPING_ERROR_CODE_KEY,
  MAXIMUM_FILE_UPLOAD,
  MAXIMUM_SIZE_UPLOAD_TO_BYTE,
  TYPE
} from '@/utils/constants/AppConstants';
import { uploadSingleFileWithType } from '@/utils/services/FileService';

import { IBasicFormEditorProps } from '.';
import { CUSTOM_EVENTS } from './constants';
import { CUSTOM_MODULES, CustomModules, FileAttachment, MentionBlot } from './modules';
import Counter from './modules/Counter';
import EmbedImageBlot, { EmbedImageBlotStatus, STATUS_EMBED_IMAGE_BLOT } from './modules/EmbedImageBlot';
import LinkBlot from './modules/LinkBlot';
import MagicUrl from './modules/MagicUrl';
import Mention from './modules/Mention';
import { convertHtmlToDelta, preserveHtmlSpaces } from './utils';

const Parchment = Quill.import('parchment');

Quill.register(`modules/${CUSTOM_MODULES.MAGIC_URL}`, MagicUrl as any);
Quill.register(`modules/${CUSTOM_MODULES.COUNTER}`, Counter as any);
Quill.register(`modules/${CUSTOM_MODULES.MENTION}`, Mention as any);
Quill.register(`modules/${CUSTOM_MODULES.FILE_ATTACHMENT}`, FileAttachment as any);

Quill.register(LinkBlot as any, true);
Quill.register(MentionBlot);
Quill.register(EmbedImageBlot);

export interface IEditorProps {
  readOnly: boolean;
  defaultValue: any;
  onChange: Function;
  onChangeSelection: Function;
  init?: (ref: Quill) => void;
  beforeDestroy?: (ref: Quill) => void;
  destroy?: () => void;
  onKeyUp?: (e: any) => void;
  onFocus?: () => void;
  modules: Record<CustomModules, any> & { toolbar?: any[] };
  theme: string;
  placeholder: string;
  editorWrapperProps?: IBasicFormEditorProps['editorWrapperProps'];
}

const allowedAttributes = [
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'link',
  'image',
  'MentionBlot',
  'EmbedImageBlot',
  'font',
  'size',
  'header',
  'blockquote',
  'code-block',
  'list',
  'script',
  'align',
  'direction'
];

function getQuillDefaultPalette(quill: Quill) {
  const toolbar: any = quill.getModule('toolbar');
  const toolbarContainer = toolbar?.container ?? null;
  if (!toolbarContainer) {
    return { colors: [], backgrounds: [] };
  }

  const colors = new Set<string>();
  const backgrounds = new Set<string>();

  toolbarContainer.querySelectorAll('.ql-color .ql-picker-item').forEach((el: Element) => {
    const value = el.getAttribute('data-value');
    if (value) colors.add(value.toLowerCase());
  });

  toolbarContainer.querySelectorAll('.ql-background .ql-picker-item').forEach((el: Element) => {
    const value = el.getAttribute('data-value');
    if (value) backgrounds.add(value.toLowerCase());
  });

  return {
    colors: Array.from(colors),
    backgrounds: Array.from(backgrounds)
  };
}

const Editor = (props: IEditorProps, ref: any) => {
  const { readOnly, defaultValue, onChange, onChangeSelection, beforeDestroy, destroy, init, modules, theme, placeholder, onKeyUp, onFocus } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const defaultValueRef = useRef(defaultValue);
  const onTextChangeRef = useRef(onChange);
  const onSelectionChangeRef = useRef(onChangeSelection);
  const onKeyUpRef = useRef(onKeyUp);
  const { magicUrl, toolbar, counter, mention, fileAttachment, history, allowEmbedImage } = modules;
  const [isActive, setIsActive] = useState<boolean>(false);

  useLayoutEffect(() => {
    onTextChangeRef.current = onChange;
    onSelectionChangeRef.current = onChangeSelection;
    onKeyUpRef.current = onKeyUp;
  });

  useEffect(() => {
    ref.current?.enable(!readOnly);
  }, [ref, readOnly]);

  useEffect(() => {
    if (!ref.current) return;
    const currentValue = ref.current.getSemanticHTML();
    if (currentValue === defaultValue) return;
    const defaultDelta = convertHtmlToDelta(ref.current, defaultValue);
    if (!defaultDelta) return;
    ref.current.setContents(defaultDelta, 'silent');
    ref.current.root?.dispatchEvent(new Event(CUSTOM_EVENTS.DEFAULT_TEXT_CHANGE));
  }, [ref, defaultValue]);

  const onComposition = (e: any, type: 'start' | 'end') => {
    const editor = e?.target;
    if (!editor) return;
    if (type === 'start') {
      editor.classList.add('ql-composition');
    } else {
      editor.classList.remove('ql-composition');
    }
  };

  const onDoubleClick = (e: any) => {
    if (!e?.target) return;
    const linkElm = Parchment.Registry?.find(e.target) as LinkBlot;

    if (linkElm && linkElm instanceof LinkBlot) {
      const { link } = linkElm.formats(e.target) ?? {};
      if (link?.href) {
        window.open(link.href, link?.target ?? '_blank');
      }
    }
  };

  const onBlur = () => {
    setIsActive(false);
  };

  const insertFile = ({
    range,
    url,
    name,
    fileStatus,
    error
  }: {
    range: Range;
    url: string;
    name: string;
    fileStatus?: EmbedImageBlotStatus;
    error?: string;
  }) => {
    const status = fileStatus ?? STATUS_EMBED_IMAGE_BLOT.UPLOADING;
    const delta = ref.current.insertEmbed(
      range.index,
      CUSTOM_MODULES.EMBED_IMAGE_BLOT,
      {
        url,
        status,
        name,
        error: error ?? ''
      },
      'user'
    );
    return delta;
  };

  const fileUploadRes = (url: string, status: EmbedImageBlotStatus, fileName: string, msgError?: string) => {
    return {
      url,
      status,
      name: fileName,
      error: msgError ?? ''
    };
  };

  const handleEmbedImageUpload = async (file: File, formData: FormData) => {
    try {
      if (file.size > MAXIMUM_SIZE_UPLOAD_TO_BYTE) {
        return fileUploadRes('', STATUS_EMBED_IMAGE_BLOT.ERROR, file.name, t('common:MSG_C_033', { maxSize: MAXIMUM_SIZE_UPLOAD_TO_BYTE }));
      }
      const { data } = await uploadSingleFileWithType('attachments', formData, {});
      if (data?.id) {
        return fileUploadRes(`${API_URL}/sta/images/${data.publicPath}`, STATUS_EMBED_IMAGE_BLOT.UPLOADED, data.name);
      }
    } catch (e: any) {
      const messageError = MAPPING_ERROR_CODE_KEY[e?.response?.data?.fields?.[0].errorCode];
      return fileUploadRes('', STATUS_EMBED_IMAGE_BLOT.ERROR, file.name, t(messageError, { files: ALLOW_ALL_FILE }));
    }
  };

  const handleEmbedImage = (range: Range, files: File[]) => {
    if (!allowEmbedImage) return;

    if (files.length > MAXIMUM_FILE_UPLOAD) {
      return dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.ERROR,
          message: t('common:MSG_C_007', { maxFiles: MAXIMUM_FILE_UPLOAD })
        })
      );
    }
    const contents = ref.current.getContents();

    const embedImageUploaded = contents.ops.filter(
      (op: any) => op?.insert?.[CUSTOM_MODULES.EMBED_IMAGE_BLOT]?.status === STATUS_EMBED_IMAGE_BLOT.UPLOADED
    );
    const embedImageUploading = contents.ops.some(
      (op: any) => op?.insert?.[CUSTOM_MODULES.EMBED_IMAGE_BLOT]?.status === STATUS_EMBED_IMAGE_BLOT.UPLOADING
    );
    if (embedImageUploading) return;
    if (embedImageUploaded.length >= MAXIMUM_FILE_UPLOAD || files.length + embedImageUploaded.length > MAXIMUM_FILE_UPLOAD) {
      return dispatch(
        setAlertNotification({
          show: true,
          type: TYPE.ERROR,
          message: t('common:MSG_C_007', { maxFiles: MAXIMUM_FILE_UPLOAD })
        })
      );
    }

    files.forEach((file: File) => {
      insertFile({ range, url: '', name: file.name });
    });
    const content = ref.current.getContents();

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      handleEmbedImageUpload(file, formData).then((data: any) => {
        const ops: Op[] = content.ops;
        const targetIndex = ops.findIndex((op) => op?.attributes?.name === file.name && op?.attributes?.status === STATUS_EMBED_IMAGE_BLOT.UPLOADING);

        const delta = insertFile({
          range,
          url: data.url,
          name: data.name,
          fileStatus: data.status,
          error: data.error ?? ''
        });
        if (targetIndex !== -1) {
          const updatedOp = delta.ops.find((el: Op) => el.attributes?.name === data.name);
          if (updatedOp) {
            ops[targetIndex] = updatedOp;
          }
        }
        ref.current.setContents(ops);
      });
    }
    ref.current.setSelection(range.index + 2);
  };

  const htmlEndsWithBlock = (html: string) => {
    html = html.trim();
    return /(<p[^>]*>|<div[^>]*>|<br\s*\/?>)\s*$/i.test(html);
  };

  useEffect(() => {
    const container = containerRef.current;
    const handleDocumentMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!containerRef.current?.contains(target)) {
        ref.current?.blur();
      }
    };

    if (container) {
      const editorBox = container.ownerDocument.createElement('div');

      // handle japanese keyboard for quilljs
      // https://github.com/quilljs/quill/issues/2237
      editorBox.addEventListener('compositionstart', (e) => onComposition(e, 'start'));
      editorBox.addEventListener('compositionend', (e) => onComposition(e, 'end'));
      editorBox.onkeyup = (e: any) => {
        onKeyUpRef.current?.(e);
      };

      const editorContainer = container.appendChild(editorBox);
      const quillOptions: QuillOptions = {
        theme,
        placeholder,
        formats: [...allowedAttributes]
      };

      quillOptions.modules = {
        uploader: {
          handler: (range: Range, files: File[]) => {
            handleEmbedImage(range, files);
          }
        }
      };
      if (toolbar) quillOptions.modules.toolbar = toolbar;
      if (magicUrl) quillOptions.modules.magicUrl = magicUrl;
      if (counter) quillOptions.modules.counter = counter;
      if (mention) quillOptions.modules.mention = mention;
      if (fileAttachment) {
        quillOptions.modules.fileAttachment = fileAttachment;
        quillOptions.modules.history = {
          delay: 2000,
          maxStack: 500,
          userOnly: true,
          ...history
        };
      }
      const quill = new Quill(editorContainer, quillOptions);

      const paletteRef = {
        colors: [] as string[],
        backgrounds: [] as string[]
      };

      const palette = getQuillDefaultPalette(quill);
      paletteRef.colors = palette.colors;
      paletteRef.backgrounds = palette.backgrounds;

      // Remove binding tab key, using default behavior
      const keyboard = quill.getModule('keyboard') as Keyboard;
      const handleDeleteKey = (range: Range, context: any) => {
        if (!range || range.length > 0) {
          quill.deleteText(range.index, range.length);
          return false;
        }

        const [line] = quill.getLine(range.index);
        if (!line) return false;

        quill.deleteText(range.index, 1);
        return false;
      };

      const handleBackspaceKey = (range: Range, context: any) => {
        if (!range || range.length > 0) {
          quill.deleteText(range.index, range.length);
          return false;
        }

        if (range.index === 0) return false;

        quill.deleteText(range.index - 1, 1);
        return false;
      };

      keyboard.addBinding({
        key: 46,
        handler: handleDeleteKey
      });

      keyboard.addBinding({
        key: 'Backspace',
        handler: handleBackspaceKey
      });
      keyboard.bindings['Tab'] && delete keyboard.bindings['Tab'];

      if (defaultValueRef.current) {
        const defVal = convertHtmlToDelta(quill, defaultValueRef.current);
        if (defVal) quill.setContents(defVal);
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      quill.root.addEventListener('dblclick', onDoubleClick);
      quill.root.addEventListener('blur', () => onBlur());

      document.addEventListener('mousedown', handleDocumentMouseDown, true);

      quill.clipboard.addMatcher('FONT', (node, delta) => {
        if (!(node instanceof HTMLElement)) return delta;
        const color = node.getAttribute('color');
        if (color) {
          delta.ops = delta.ops.map((op) => ({
            ...op,
            attributes: {
              ...op.attributes,
              color: color.startsWith('#') ? color : `#${color}`
            }
          }));
        }
        return delta;
      });

      quill.clipboard.addMatcher('INS', (node, delta) => {
        delta.ops = delta.ops.map((op) => ({
          ...op,
          attributes: {
            ...op.attributes,
            underline: true
          }
        }));
        return delta;
      });

      quill.clipboard.addMatcher('DEL', (node, delta) => {
        delta.ops = delta.ops.map((op) => ({
          ...op,
          attributes: {
            ...op.attributes,
            strike: true
          }
        }));
        return delta;
      });

      quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
        if (!(node instanceof HTMLElement)) return delta;
        const computed = window.getComputedStyle(node);

        const color = computed.color;
        const background = computed.backgroundColor;

        if (color || background) {
          delta.ops = delta.ops.map((op) => ({
            ...op,
            attributes: {
              ...op.attributes,
              ...(color ? { color } : {}),
              ...(background ? { background } : {})
            }
          }));
        }
        return delta;
      });
      const cleanPasteAttributes = (attributes: any) => {
        if (!attributes) return undefined;

        const cleaned = Object.fromEntries(
          Object.entries(attributes).filter(([key]) => {
            return key !== 'MentionBlot' && key !== 'EmbedImageBlot';
          })
        );

        return Object.keys(cleaned).length ? cleaned : undefined;
      };
      quill.clipboard.onPaste = async (range: any, { text, html }: { text?: string; html?: string }) => {
        const flatDelta = new Delta();
        if (html) {
          const pastedDelta = quill.clipboard.convert({ html: preserveHtmlSpaces(html) });
          pastedDelta.ops.forEach((op: any, i: number) => {
            if (typeof op.insert === 'string') {
              let text = op.insert.replaceAll('__FULLWIDTH_SPACE__', '\u3000');
              if (i === pastedDelta.ops.length - 1 && text.endsWith('\n') && !htmlEndsWithBlock(html)) {
                text = text.replace(/\n$/, '');
              }

              flatDelta.insert(text, cleanPasteAttributes(op.attributes));
            }
          });
        } else if (text) {
          const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+|\b[a-z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?)$/i;
          if (urlRegex.test(text.trim())) {
            flatDelta.insert(text.trim(), { link: text.trim() });
          } else {
            flatDelta.insert(text);
          }
        }
        if (range.length > 0) {
          quill.deleteText(range.index, range.length, 'silent');
        }

        quill.updateContents(new Delta().retain(range.index).concat(flatDelta), 'user');

        const pasteEndIndex =
          range.index +
          flatDelta.reduce((length, op) => {
            if (typeof op.insert === 'string') {
              return length + op.insert.length;
            }

            return length + 1;
          }, 0);

        quill.setSelection(pasteEndIndex, 0, 'silent');
        quill.scrollSelectionIntoView();
      };

      ref.current = quill;
      init && init(quill);
    }
    return () => {
      beforeDestroy && beforeDestroy(ref.current);
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
      ref.current = null;
      if (container) container.innerHTML = '';
      destroy && destroy();
    };
  }, []);

  return (
    <div
      className={classNames(isActive ? 'active' : '', 'quill-wrapper', props.editorWrapperProps?.className)}
      style={{ ...props.editorWrapperProps?.style }}
      ref={containerRef}
      onFocus={() => {
        setIsActive(true);
        onFocus && onFocus();
      }}
      onBlur={() => setIsActive(false)}
    ></div>
  );
};

export default forwardRef(Editor);
